import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import ClientAppShell from "../components/ClientAppShell";
import {
  getIndustries,
  getSubSectors,
  getBusinessFunctions,
  getDataAIValueLevers,
  getBusinessValueDrivers,
  getFunctionalValueDrivers,
  getCapabilityAreas,
  getUseCases
} from "../services/CostEstimationsetupApi";
import { getSMEPendingProjectList } from "../services/smeApi";
import { getBusinessValueCalculationByProject } from "../services/valueEstimationApi";
import {
  dedupePendingProjects,
  getClientUserId,
  getCompanyName,
  getPendingProjectKey,
  getProjectId,
  getProjectName,
  type SmePendingProject
} from "../utils/smeProjectUtils";
import { BarChart, ClipboardList, DollarSign, Factory, LineChart, Save, Settings, Users, X } from "lucide-react";
import "../assets/css/value-estimation.css";
const EMPTY_FORM = {
  projectKey: "",
  projectId: 0,
  clientUserId: 0,
  estimationId: 0,
  companyName: "",
  industryId: 0,
  subSectorId: 0,
  projectTitle: "",
  description: "",
  businessFunction: "",
  subBusinessFunction: "",
  capabilityArea: "",
  dataAIValueLever: "",
  primaryBusinessValueDriver: "",
  secondaryBusinessValueDriver: "",
  functionalValueDriver: "",
  currentEmployeesCount: 0,
  averageCostPerEmployee: 0,
  expectedEmployeesAfterInitiative: 0,
  currentErrorRate: 0,
  expectedErrorRatePostImplementation: 0,
  downtimeReductionPercentage: 0,
  durationInYears: 0,
  selectedUseCaseIds: [] as number[],
  oneTimeImplementationCost: 0,
  ongoingMonthlyCost: 0,
  isCompleted: false
};

const extractListData = (response: unknown): unknown[] => {
  if (!response || typeof response !== "object") return [];
  const root = response as Record<string, unknown>;
  const payload = root.data ?? response;

  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    const nested = payload as Record<string, unknown>;
    if (Array.isArray(nested.data)) return nested.data;
    if (Array.isArray(nested.items)) return nested.items;
  }
  return [];
};

const ValueEstimation = () => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  // Dropdown data states
  const [industries, setIndustries] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [businessFunctions, setBusinessFunctions] = useState([]);
  const [subBusinessFunctions, setSubBusinessFunctions] = useState([]);
  const [dataAILevers, setDataAILevers] = useState([]);
  const [businessValueDrivers, setBusinessValueDrivers] = useState([]);
  const [functionalValueDrivers, setFunctionalValueDrivers] = useState([]);
  const [useCases, setUseCases] = useState([]);
  
  // Loading states
  const [industriesLoading, setIndustriesLoading] = useState(false);
  const [sectorsLoading, setSectorsLoading] = useState(false);
  const [businessFunctionsLoading, setBusinessFunctionsLoading] = useState(false);
  const [dataAILeversLoading, setDataAILeversLoading] = useState(false);
  const [businessValueDriversLoading, setBusinessValueDriversLoading] = useState(false);
  const [functionalValueDriversLoading, setFunctionalValueDriversLoading] = useState(false);
  const [pendingProjects, setPendingProjects] = useState<SmePendingProject[]>([]);
  const [pendingProjectsLoading, setPendingProjectsLoading] = useState(false);
  const [capabilityAreas, setCapabilityAreas] = useState<Array<{ id: number; capabilityName: string }>>([]);
  const [capabilityAreasLoading, setCapabilityAreasLoading] = useState(false);
  const [estimationLoading, setEstimationLoading] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const companyOptions = useMemo(() => {
    const names = new Set<string>();
    pendingProjects.forEach((project) => {
      const company = getCompanyName(project);
      if (company) names.add(company);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [pendingProjects]);

  const projectOptionsForCompany = useMemo(() => {
    if (!formData.companyName) return [];
    return pendingProjects.filter(
      (project) => getCompanyName(project) === formData.companyName
    );
  }, [pendingProjects, formData.companyName]);

  const fetchPendingProjects = useCallback(async () => {
    try {
      setPendingProjectsLoading(true);
      const response = await getSMEPendingProjectList();
      const rawProjects =
        response?.data?.projects ??
        response?.projects ??
        (Array.isArray(response?.data) ? response.data : []);
      setPendingProjects(dedupePendingProjects(Array.isArray(rawProjects) ? rawProjects : []));
    } catch (err) {
      console.error("Error fetching SME assigned projects:", err);
      setPendingProjects([]);
      toast.error("Failed to load assigned companies and projects");
    } finally {
      setPendingProjectsLoading(false);
    }
  }, []);

  const fetchCapabilityAreasList = useCallback(async () => {
    try {
      setCapabilityAreasLoading(true);
      const response = await getCapabilityAreas({
        isActive: true,
        page: 1,
        pageSize: 1000
      });
      const items = extractListData(response) as Array<{ id: number; capabilityName: string }>;
      setCapabilityAreas(items);
    } catch (err) {
      console.error("Error fetching capability areas:", err);
      setCapabilityAreas([]);
    } finally {
      setCapabilityAreasLoading(false);
    }
  }, []);

  const fetchUseCasesList = useCallback(async () => {
    try {
      const response = await getUseCases({
        isActive: true,
        page: 1,
        pageSize: 1000
      });
      const items = extractListData(response) as Array<{ id: number; title: string }>;
      setUseCases(items);
    } catch (err) {
      console.error("Error fetching use cases:", err);
      setUseCases([]);
    }
  }, []);

  // Fetch Industries API
  const fetchIndustries = useCallback(async () => {
    try {
      setIndustriesLoading(true);
      const response = await getIndustries({
        isActive: true,
        page: 1,
        pageSize: 1000
      });
      
      if (response) {
        setIndustries(extractListData(response) as typeof industries);
      } else {
        setIndustries([]);
      }
    } catch (err) {
      console.error('Error fetching industries:', err);
      setIndustries([]);
    } finally {
      setIndustriesLoading(false);
    }
  }, []);

  // Fetch Sectors API
  const fetchSectors = useCallback(async (industryId = null) => {
    try {
      setSectorsLoading(true);
      
      const params = {
        isActive: true,
        page: 1,
        pageSize: 1000
      };
      
      if (industryId) {
        params.industryId = industryId;
      }
      
      const response = await getSubSectors(params);
      
      if (response) {
        setSectors(extractListData(response) as typeof sectors);
      } else {
        setSectors([]);
      }
    } catch (err) {
      console.error('Error fetching sectors:', err);
      setSectors([]);
    } finally {
      setSectorsLoading(false);
    }
  }, []);

  // Fetch Business Functions API
  const fetchBusinessFunctions = useCallback(async () => {
    try {
      setBusinessFunctionsLoading(true);
      const response = await getBusinessFunctions({
        isActive: true,
        page: 1,
        pageSize: 1000
      });
      
      if (response) {
        const functions = extractListData(response) as Array<{
          id: number;
          name: string;
          subBusinessFunctions?: Array<{ id: number; name: string }>;
        }>;
        setBusinessFunctions(functions);

        const allSubFunctions: typeof subBusinessFunctions = [];
        functions.forEach((func) => {
          if (func.subBusinessFunctions && Array.isArray(func.subBusinessFunctions)) {
            func.subBusinessFunctions.forEach((subFunc) => {
              allSubFunctions.push({
                ...subFunc,
                functionId: func.id,
                functionName: func.name
              });
            });
          }
        });
        setSubBusinessFunctions(allSubFunctions);
      } else {
        setBusinessFunctions([]);
        setSubBusinessFunctions([]);
      }
    } catch (err) {
      console.error('Error fetching business functions:', err);
      setBusinessFunctions([]);
      setSubBusinessFunctions([]);
    } finally {
      setBusinessFunctionsLoading(false);
    }
  }, []);

  // Fetch Data/AI Value Levers API
  const fetchDataAIValueLevers = useCallback(async () => {
    try {
      setDataAILeversLoading(true);
      const response = await getDataAIValueLevers({
        isActive: true,
        page: 1,
        pageSize: 1000
      });
      
      if (response) {
        setDataAILevers(extractListData(response) as typeof dataAILevers);
      } else {
        setDataAILevers([]);
      }
    } catch (err) {
      console.error('Error fetching data/AI levers:', err);
      setDataAILevers([]);
    } finally {
      setDataAILeversLoading(false);
    }
  }, []);

  // Fetch Business Value Drivers API
  const fetchBusinessValueDrivers = useCallback(async () => {
    try {
      setBusinessValueDriversLoading(true);
      const response = await getBusinessValueDrivers({
        isActive: true,
        page: 1,
        pageSize: 1000
      });
      
      if (response) {
        setBusinessValueDrivers(extractListData(response) as typeof businessValueDrivers);
      } else {
        setBusinessValueDrivers([]);
      }
    } catch (err) {
      console.error('Error fetching business value drivers:', err);
      setBusinessValueDrivers([]);
    } finally {
      setBusinessValueDriversLoading(false);
    }
  }, []);

  // Fetch Functional Value Drivers API
  const fetchFunctionalValueDrivers = useCallback(async () => {
    try {
      setFunctionalValueDriversLoading(true);
      const response = await getFunctionalValueDrivers({
        isActive: true,
        page: 1,
        pageSize: 1000
      });
      
      if (response) {
        setFunctionalValueDrivers(extractListData(response) as typeof functionalValueDrivers);
      } else {
        setFunctionalValueDrivers([]);
      }
    } catch (err) {
      console.error('Error fetching functional value drivers:', err);
      setFunctionalValueDrivers([]);
    } finally {
      setFunctionalValueDriversLoading(false);
    }
  }, []);

  const applySavedEstimation = useCallback((saved: Record<string, unknown>) => {
    if (!saved) return;

    const selectedUseCaseIds = Array.isArray(saved.selectedUseCaseIds)
      ? saved.selectedUseCaseIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
      : [];

    setFormData((prev) => ({
      ...prev,
      estimationId: Number(saved.id) || 0,
      companyName: String(saved.companyName ?? prev.companyName ?? ""),
      industryId: Number(saved.industryId) || 0,
      subSectorId: Number(saved.subSectorId) || 0,
      projectTitle: String(saved.projectTitle ?? prev.projectTitle ?? ""),
      description: String(saved.description ?? ""),
      businessFunction: String(saved.businessFunction ?? ""),
      subBusinessFunction: String(saved.subBusinessFunction ?? ""),
      capabilityArea: String(saved.capabilityArea ?? ""),
      dataAIValueLever: String(saved.dataAIValueLever ?? ""),
      primaryBusinessValueDriver: String(saved.primaryBusinessValueDriver ?? ""),
      secondaryBusinessValueDriver: String(saved.secondaryBusinessValueDriver ?? ""),
      functionalValueDriver: String(saved.functionalValueDriver ?? ""),
      currentEmployeesCount: Number(saved.currentEmployeesCount) || 0,
      averageCostPerEmployee: Number(saved.averageCostPerEmployee) || 0,
      expectedEmployeesAfterInitiative: Number(saved.expectedEmployeesAfterInitiative) || 0,
      currentErrorRate: Number(saved.currentErrorRate) || 0,
      expectedErrorRatePostImplementation: Number(saved.expectedErrorRatePostImplementation) || 0,
      downtimeReductionPercentage: Number(saved.downtimeReductionPercentage) || 0,
      durationInYears: Number(saved.durationInYears) || 0,
      selectedUseCaseIds,
      oneTimeImplementationCost: Number(saved.oneTimeImplementationCost) || 0,
      ongoingMonthlyCost: Number(saved.ongoingMonthlyCost) || 0,
      isCompleted: Boolean(saved.isCompleted),
    }));

    const industryId = Number(saved.industryId) || 0;
    if (industryId) {
      fetchSectors(industryId);
    }

    setIsReadOnly(Boolean(saved.isCompleted));
  }, [fetchSectors]);

  const loadSavedEstimation = useCallback(async (projectId: number) => {
    if (!projectId) return;

    try {
      setEstimationLoading(true);
      const saved = await getBusinessValueCalculationByProject(projectId);
      if (saved) {
        applySavedEstimation(saved as Record<string, unknown>);
      } else {
        setIsReadOnly(false);
      }
    } catch (err) {
      console.error("Error loading saved value estimation:", err);
      setIsReadOnly(false);
    } finally {
      setEstimationLoading(false);
    }
  }, [applySavedEstimation]);

  // Load data on component mount
  useEffect(() => {
    fetchIndustries();
    fetchSectors();
    fetchBusinessFunctions();
    fetchDataAIValueLevers();
    fetchBusinessValueDrivers();
    fetchFunctionalValueDrivers();
    fetchPendingProjects();
    fetchCapabilityAreasList();
    fetchUseCasesList();
  }, [
    fetchIndustries,
    fetchSectors,
    fetchBusinessFunctions,
    fetchDataAIValueLevers,
    fetchBusinessValueDrivers,
    fetchFunctionalValueDrivers,
    fetchPendingProjects,
    fetchCapabilityAreasList,
    fetchUseCasesList
  ]);

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;

    if (isReadOnly && name !== "projectKey" && name !== "companyName") {
      return;
    }
    
    if (name === "companyName") {
      setFormData((prev) => ({
        ...EMPTY_FORM,
        companyName: value,
      }));
      setSectors([]);
      setIsReadOnly(false);
      return;
    }

    if (name === "projectKey") {
      const selectedProject = projectOptionsForCompany.find(
        (project) => getPendingProjectKey(project) === value
      );

      if (!selectedProject) {
        setFormData((prev) => ({
          ...prev,
          projectKey: "",
          projectId: 0,
          clientUserId: 0,
          projectTitle: "",
          estimationId: 0,
        }));
        setIsReadOnly(false);
        return;
      }

      const projectId = getProjectId(selectedProject) ?? 0;
      const clientUserId = getClientUserId(selectedProject) ?? 0;

      setFormData((prev) => ({
        ...EMPTY_FORM,
        companyName: getCompanyName(selectedProject),
        projectKey: value,
        projectId,
        clientUserId,
        projectTitle: getProjectName(selectedProject),
      }));
      setSectors([]);
      setIsReadOnly(false);

      if (projectId) {
        loadSavedEstimation(projectId);
      }
      return;
    }

    if (name === "industryId") {
      const industryId = parseInt(value, 10) || 0;
      setFormData((prev) => ({
        ...prev,
        industryId,
        subSectorId: 0,
      }));

      if (industryId) {
        fetchSectors(industryId);
      } else {
        setSectors([]);
      }
    } else if (name === "businessFunction") {
      setFormData((prev) => ({
        ...prev,
        businessFunction: value,
        subBusinessFunction: "",
      }));
    } else if (name === "selectedUseCaseIds") {
      const selectedIds = Array.from(
        e.target.selectedOptions,
        (option) => parseInt(option.value, 10)
      ).filter((id) => !Number.isNaN(id));
      setFormData((prev) => ({
        ...prev,
        selectedUseCaseIds: selectedIds,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]:
          type === "checkbox"
            ? checked
            : type === "number"
              ? parseFloat(value) || 0
              : value,
      }));
    }
  }, [fetchSectors, isReadOnly, loadSavedEstimation, projectOptionsForCompany]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (!formData.companyName || !formData.projectTitle || !formData.projectId) {
        toast.error("Please select a company and project");
        return;
      }

      if (!formData.industryId || !formData.subSectorId) {
        toast.error("Please fill in all required fields");
        return;
      }

      // Submit is intentionally not wired yet.
      // Target endpoint: POST /CostEstimation/AddOrUpdateBusinessValueCalculation
      // Payload shape: SaveBusinessValueCalculatorDto (see valueEstimationApi.tsx)
      console.log("Value estimation payload (not submitted yet):", formData);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success("Value estimation validated — backend submit not connected yet");
      
    } catch (err) {
      console.error('Error submitting form:', err);
      toast.error("Failed to submit value estimation");
    } finally {
      setLoading(false);
    }
  }, [formData]);

  const resetForm = useCallback(() => {
    setFormData({ ...EMPTY_FORM });
    setSectors([]);
    setIsReadOnly(false);
  }, []);

  return (
    <ClientAppShell
      title="Value Estimation"
      dataNav="value-estimation"
      selectedItem={selectedItem}
      onMenuSelect={handleMenuSelection}
      intro={{
        eyebrow: 'Business value',
        description:
          'Estimate the value of client Data + AI initiatives—choose company and project, then capture drivers, assumptions, and expected outcomes.',
      }}
    >
      <div className="dash-frame-wrapper">
        <div className="dash-frame-div">
          <div className="value-estimation-form-root">
            {estimationLoading && (
              <div className="value-estimation-form-group" style={{ marginBottom: "1rem" }}>
                Loading saved value estimation...
              </div>
            )}
            <form onSubmit={handleSubmit} className="value-estimation-form">
                {/* Company Information */}
                <div className="value-estimation-section">
                  <h5 className="value-estimation-section-title">
                    <Factory className="icon" />
                    Company Information
                  </h5>
                </div>

                <div className="value-estimation-row">
                  <div className="value-estimation-col-6">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label required">Company Name</label>
                      <select
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        required
                        disabled={pendingProjectsLoading}
                        className="value-estimation-select"
                      >
                        <option value="">
                          {pendingProjectsLoading
                            ? "Loading assigned companies..."
                            : companyOptions.length === 0
                              ? "No assigned companies"
                              : "Select Company"}
                        </option>
                        {companyOptions.map((company) => (
                          <option key={company} value={company}>
                            {company}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="value-estimation-col-6">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label required">Project Title</label>
                      <select
                        name="projectKey"
                        value={formData.projectKey}
                        onChange={handleInputChange}
                        required
                        disabled={
                          !formData.companyName ||
                          pendingProjectsLoading ||
                          estimationLoading
                        }
                        className="value-estimation-select"
                      >
                        <option value="">
                          {!formData.companyName
                            ? "Select a company first"
                            : projectOptionsForCompany.length === 0
                              ? "No projects for this company"
                              : "Select Project"}
                        </option>
                        {projectOptionsForCompany.map((project) => (
                          <option
                            key={getPendingProjectKey(project)}
                            value={getPendingProjectKey(project)}
                          >
                            {getProjectName(project)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="value-estimation-row">
                  <div className="value-estimation-col-6">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label required">Industry</label>
                      <select
                        name="industryId"
                        value={formData.industryId}
                        onChange={handleInputChange}
                        required
                        disabled={industriesLoading || isReadOnly}
                        className="value-estimation-select"
                      >
                        <option value={0}>
                          {industriesLoading ? 'Loading industries...' : 'Select Industry'}
                        </option>
                        {industries.map(industry => (
                          <option key={industry.id} value={industry.id}>
                            {industry.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="value-estimation-col-6">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label required">Sub Sector</label>
                      <select
                        name="subSectorId"
                        value={formData.subSectorId}
                        onChange={handleInputChange}
                        required
                        disabled={!formData.industryId || sectorsLoading || isReadOnly}
                        className="value-estimation-select"
                      >
                        <option value={0}>
                          {sectorsLoading ? 'Loading sectors...' : 'Select Sub Sector'}
                        </option>
                        {sectors.map(sector => (
                          <option key={sector.id} value={sector.id}>
                            {sector.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="value-estimation-row">
                  <div className="value-estimation-col-12">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Project Description</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Enter project description"
                        rows={3}
                        disabled={isReadOnly}
                        className="value-estimation-textarea"
                      />
                    </div>
                  </div>
                </div>

                {/* Business Function Information */}
                <div className="value-estimation-section">
                  <h5 className="value-estimation-section-title">
                    <Settings className="icon" />
                    Business Function Information
                  </h5>
                </div>

                <div className="value-estimation-row">
                  <div className="value-estimation-col-6">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Business Function</label>
                      <select
                        name="businessFunction"
                        value={formData.businessFunction}
                        onChange={handleInputChange}
                        disabled={businessFunctionsLoading || isReadOnly}
                        className="value-estimation-select"
                      >
                        <option value="">
                          {businessFunctionsLoading ? 'Loading functions...' : 'Select Business Function'}
                        </option>
                        {businessFunctions.map(func => (
                          <option key={func.id} value={func.name}>
                            {func.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="value-estimation-col-6">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Sub Business Function</label>
                      <select
                        name="subBusinessFunction"
                        value={formData.subBusinessFunction}
                        onChange={handleInputChange}
                        disabled={!formData.businessFunction || businessFunctionsLoading || isReadOnly}
                        className="value-estimation-select"
                      >
                        <option value="">
                          {businessFunctionsLoading ? 'Loading sub-functions...' : 'Select Sub Business Function'}
                        </option>
                        {subBusinessFunctions
                          .filter(subFunc => subFunc.functionName === formData.businessFunction)
                          .map(subFunc => (
                            <option key={subFunc.id} value={subFunc.name}>
                              {subFunc.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="value-estimation-row">
                  <div className="value-estimation-col-6">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Capability Area</label>
                      <select
                        name="capabilityArea"
                        value={formData.capabilityArea}
                        onChange={handleInputChange}
                        disabled={capabilityAreasLoading || isReadOnly}
                        className="value-estimation-select"
                      >
                        <option value="">
                          {capabilityAreasLoading ? "Loading capability areas..." : "Select Capability Area"}
                        </option>
                        {capabilityAreas.map((area) => (
                          <option key={area.id} value={area.capabilityName}>
                            {area.capabilityName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="value-estimation-col-6">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Data/AI Value Lever</label>
                      <select
                        name="dataAIValueLever"
                        value={formData.dataAIValueLever}
                        onChange={handleInputChange}
                        disabled={dataAILeversLoading || isReadOnly}
                        className="value-estimation-select"
                      >
                        <option value="">
                          {dataAILeversLoading ? 'Loading levers...' : 'Select Data/AI Value Lever'}
                        </option>
                        {dataAILevers.map(lever => (
                          <option key={lever.id} value={lever.name}>
                            {lever.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Value Drivers */}
                <div className="value-estimation-section">
                  <h5 className="value-estimation-section-title">
                    <LineChart className="icon" />
                    Value Drivers
                  </h5>
                </div>

                <div className="value-estimation-row">
                  <div className="value-estimation-col-4">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Primary Business Value Driver</label>
                      <select
                        name="primaryBusinessValueDriver"
                        value={formData.primaryBusinessValueDriver}
                        onChange={handleInputChange}
                        disabled={businessValueDriversLoading || isReadOnly}
                        className="value-estimation-select"
                      >
                        <option value="">
                          {businessValueDriversLoading ? 'Loading drivers...' : 'Select Primary Business Value Driver'}
                        </option>
                        {businessValueDrivers.map(driver => (
                          <option key={driver.id} value={driver.name}>
                            {driver.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="value-estimation-col-4">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Secondary Business Value Driver</label>
                      <select
                        name="secondaryBusinessValueDriver"
                        value={formData.secondaryBusinessValueDriver}
                        onChange={handleInputChange}
                        disabled={businessValueDriversLoading || isReadOnly}
                        className="value-estimation-select"
                      >
                        <option value="">
                          {businessValueDriversLoading ? 'Loading drivers...' : 'Select Secondary Business Value Driver'}
                        </option>
                        {businessValueDrivers.map(driver => (
                          <option key={driver.id} value={driver.name}>
                            {driver.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="value-estimation-col-4">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Functional Value Driver</label>
                      <select
                        name="functionalValueDriver"
                        value={formData.functionalValueDriver}
                        onChange={handleInputChange}
                        disabled={functionalValueDriversLoading || isReadOnly}
                        className="value-estimation-select"
                      >
                        <option value="">
                          {functionalValueDriversLoading ? 'Loading drivers...' : 'Select Functional Value Driver'}
                        </option>
                        {functionalValueDrivers.map(driver => (
                          <option key={driver.id} value={driver.name}>
                            {driver.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Employee Information */}
                <div className="value-estimation-section">
                  <h5 className="value-estimation-section-title">
                    <Users className="icon" />
                    Employee Information
                  </h5>
                </div>

                <div className="value-estimation-row">
                  <div className="value-estimation-col-4">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Current Employees Count</label>
                      <input
                        type="number"
                        name="currentEmployeesCount"
                        value={formData.currentEmployeesCount}
                        onChange={handleInputChange}
                        placeholder="Enter current employee count"
                        min="0"
                        disabled={isReadOnly}
                        className="value-estimation-input"
                      />
                    </div>
                  </div>
                  <div className="value-estimation-col-4">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Average Cost Per Employee ($)</label>
                      <input
                        type="number"
                        name="averageCostPerEmployee"
                        value={formData.averageCostPerEmployee}
                        onChange={handleInputChange}
                        placeholder="Enter average cost per employee"
                        min="0"
                        step="0.01"
                        disabled={isReadOnly}
                        className="value-estimation-input"
                      />
                    </div>
                  </div>
                  <div className="value-estimation-col-4">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Expected Employees After Initiative</label>
                      <input
                        type="number"
                        name="expectedEmployeesAfterInitiative"
                        value={formData.expectedEmployeesAfterInitiative}
                        onChange={handleInputChange}
                        placeholder="Enter expected employee count"
                        min="0"
                        disabled={isReadOnly}
                        className="value-estimation-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Performance Metrics */}
                <div className="value-estimation-section">
                  <h5 className="value-estimation-section-title">
                    <BarChart className="icon" />
                    Performance Metrics
                  </h5>
                </div>

                <div className="value-estimation-row">
                  <div className="value-estimation-col-4">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Current Error Rate (%)</label>
                      <input
                        type="number"
                        name="currentErrorRate"
                        value={formData.currentErrorRate}
                        onChange={handleInputChange}
                        placeholder="Enter current error rate"
                        min="0"
                        max="100"
                        step="0.1"
                        disabled={isReadOnly}
                        className="value-estimation-input"
                      />
                    </div>
                  </div>
                  <div className="value-estimation-col-4">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Expected Error Rate Post Implementation (%)</label>
                      <input
                        type="number"
                        name="expectedErrorRatePostImplementation"
                        value={formData.expectedErrorRatePostImplementation}
                        onChange={handleInputChange}
                        placeholder="Enter expected error rate"
                        min="0"
                        max="100"
                        step="0.1"
                        disabled={isReadOnly}
                        className="value-estimation-input"
                      />
                    </div>
                  </div>
                  <div className="value-estimation-col-4">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Downtime Reduction Percentage (%)</label>
                      <input
                        type="number"
                        name="downtimeReductionPercentage"
                        value={formData.downtimeReductionPercentage}
                        onChange={handleInputChange}
                        placeholder="Enter downtime reduction percentage"
                        min="0"
                        max="100"
                        step="0.1"
                        disabled={isReadOnly}
                        className="value-estimation-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Project Details */}
                <div className="value-estimation-section">
                  <h5 className="value-estimation-section-title">
                    <ClipboardList className="icon" />
                    Project Details
                  </h5>
                </div>

                <div className="value-estimation-row">
                  <div className="value-estimation-col-6">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Duration (Years)</label>
                      <input
                        type="number"
                        name="durationInYears"
                        value={formData.durationInYears}
                        onChange={handleInputChange}
                        placeholder="Enter project duration in years"
                        min="0"
                        step="0.1"
                        disabled={isReadOnly}
                        className="value-estimation-input"
                      />
                    </div>
                  </div>
                  <div className="value-estimation-col-6">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Selected Use Cases</label>
                      <select
                        name="selectedUseCaseIds"
                        value={formData.selectedUseCaseIds.map(String)}
                        onChange={handleInputChange}
                        multiple
                        size={4}
                        disabled={isReadOnly}
                        className="value-estimation-select"
                      >
                        {useCases.map(useCase => (
                          <option key={useCase.id} value={useCase.id}>
                            {useCase.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Cost Information */}
                <div className="value-estimation-section">
                  <h5 className="value-estimation-section-title">
                    <DollarSign className="icon" />
                    Cost Information
                  </h5>
                </div>

                <div className="value-estimation-row">
                  <div className="value-estimation-col-6">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">One-Time Implementation Cost ($)</label>
                      <input
                        type="number"
                        name="oneTimeImplementationCost"
                        value={formData.oneTimeImplementationCost}
                        onChange={handleInputChange}
                        placeholder="Enter one-time implementation cost"
                        min="0"
                        step="0.01"
                        disabled={isReadOnly}
                        className="value-estimation-input"
                      />
                    </div>
                  </div>
                  <div className="value-estimation-col-6">
                    <div className="value-estimation-form-group">
                      <label className="value-estimation-label">Ongoing Monthly Cost ($)</label>
                      <input
                        type="number"
                        name="ongoingMonthlyCost"
                        value={formData.ongoingMonthlyCost}
                        onChange={handleInputChange}
                        placeholder="Enter ongoing monthly cost"
                        min="0"
                        step="0.01"
                        disabled={isReadOnly}
                        className="value-estimation-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Status */}
                <div className="value-estimation-section">
                  <div className="value-estimation-form-group">
                    <label className="value-estimation-checkbox-label">
                      <input
                        type="checkbox"
                        name="isCompleted"
                        checked={formData.isCompleted}
                        onChange={handleInputChange}
                        disabled={isReadOnly}
                        className="value-estimation-checkbox"
                      />
                      Project Completed
                    </label>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="value-estimation-button-group">
                  <button
                    type="button"
                    onClick={resetForm}
                    disabled={loading}
                    className="btn-dashboard-cancel"
                  >
                    <X />
                    Reset
                  </button>
                  <button
                    type="submit"
                    disabled={loading || isReadOnly}
                    className="btn-dashboard-primary"
                  >
                    {loading && <span className="value-estimation-spinner"></span>}
                    <Save />
                    {loading ? "Submitting..." : "Submit Value Estimation"}
                  </button>
                </div>
            </form>
          </div>
        </div>
      </div>
    </ClientAppShell>
  );
};

export default ValueEstimation;
